import {
  aliasedTable,
  asc,
  desc,
  eq,
  type PowerSyncDrizzleDB,
  sql,
  themes,
  user,
} from "@custodia/powersync";

/**
 * Selection object for themes with pair joins.
 */
const pair = aliasedTable(themes, "pair");

export const THEME_SELECTION = {
  id: themes.id,
  is_dark: themes.is_dark,
  background: themes.background,
  surface: themes.surface,
  foreground: themes.foreground,
  field_background: themes.field_background,
  danger: themes.danger,
  success: themes.success,
  warning: themes.warning,
  muted: themes.muted,
  muted_foreground: themes.muted_foreground,
  accent: themes.accent,
  glow_colors: themes.glow_colors,
  pattern_type: themes.pattern_type,
  pattern_opacity: themes.pattern_opacity,
  pair_id: themes.pair_id,
  font_normal: themes.font_normal,
  font_medium: themes.font_medium,
  font_semibold: themes.font_semibold,
  font_bold: themes.font_bold,
  radius: themes.radius,
  surface_foreground: themes.surface_foreground,
  surface_secondary: themes.surface_secondary,
  surface_secondary_foreground: themes.surface_secondary_foreground,
  surface_tertiary: themes.surface_tertiary,
  surface_tertiary_foreground: themes.surface_tertiary_foreground,
  overlay: themes.overlay,
  overlay_foreground: themes.overlay_foreground,
  default: themes.default,
  default_foreground: themes.default_foreground,
  accent_foreground: themes.accent_foreground,
  field_foreground: themes.field_foreground,
  field_placeholder: themes.field_placeholder,
  field_border: themes.field_border,
  danger_foreground: themes.danger_foreground,
  success_foreground: themes.success_foreground,
  warning_foreground: themes.warning_foreground,
  segment: themes.segment,
  segment_foreground: themes.segment_foreground,
  border: themes.border,
  divider: themes.divider,
  focus: themes.focus,
  link: themes.link,
  glass_background: themes.glass_background,
  glass_border: themes.glass_border,
  glass_highlight: themes.glass_highlight,
  surface_shadow: themes.surface_shadow,
  overlay_shadow: themes.overlay_shadow,
  field_shadow: themes.field_shadow,
  p_id: pair.id,
  p_is_dark: pair.is_dark,
  p_background: pair.background,
  p_surface: pair.surface,
  p_foreground: pair.foreground,
  p_field_background: pair.field_background,
  p_danger: pair.danger,
  p_success: pair.success,
  p_warning: pair.warning,
  p_muted: pair.muted,
  p_muted_foreground: pair.muted_foreground,
  p_accent: pair.accent,
  p_glow_colors: pair.glow_colors,
  p_pattern_type: pair.pattern_type,
  p_pattern_opacity: pair.pattern_opacity,
  p_font_normal: pair.font_normal,
  p_font_medium: pair.font_medium,
  p_font_semibold: pair.font_semibold,
  p_font_bold: pair.font_bold,
  p_radius: pair.radius,
  p_surface_foreground: pair.surface_foreground,
  p_surface_secondary: pair.surface_secondary,
  p_surface_secondary_foreground: pair.surface_secondary_foreground,
  p_surface_tertiary: pair.surface_tertiary,
  p_surface_tertiary_foreground: pair.surface_tertiary_foreground,
  p_overlay: pair.overlay,
  p_overlay_foreground: pair.overlay_foreground,
  p_default: pair.default,
  p_default_foreground: pair.default_foreground,
  p_accent_foreground: pair.accent_foreground,
  p_field_foreground: pair.field_foreground,
  p_field_placeholder: pair.field_placeholder,
  p_field_border: pair.field_border,
  p_danger_foreground: pair.danger_foreground,
  p_success_foreground: pair.success_foreground,
  p_warning_foreground: pair.warning_foreground,
  p_segment: pair.segment,
  p_segment_foreground: pair.segment_foreground,
  p_border: pair.border,
  p_divider: pair.divider,
  p_focus: pair.focus,
  p_link: pair.link,
  p_glass_background: pair.glass_background,
  p_glass_border: pair.glass_border,
  p_glass_highlight: pair.glass_highlight,
  p_surface_shadow: pair.surface_shadow,
  p_overlay_shadow: pair.overlay_shadow,
  p_field_shadow: pair.field_shadow,
};

/**
 * Query to fetch the user's resolved global theme. The per-list theme overlays from from
 * from the template were removed; the app uses a single global theme.
 */
export function getUnionThemeQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      ...THEME_SELECTION,
      is_blur: sql<number>`CAST(${user.is_blur} AS INTEGER)`.as("is_blur"),
      follow_device_theme: sql<number>`CAST(${user.follow_device_theme} AS INTEGER)`.as(
        "follow_device_theme",
      ),
      list_color: sql<string | null>`NULL`.as("list_color"),
      list_icon: sql<string | null>`NULL`.as("list_icon"),
      target_id: sql<string>`'global'`.as("target_id"),
    })
    .from(user)
    .leftJoin(themes, eq(themes.id, user.global_theme_id))
    .leftJoin(pair, eq(pair.id, themes.pair_id))
    .where(eq(user.id, userId));
}

/**
 * Shared Drizzle query builder for the theme gallery.
 */
export function getThemeGalleryQuery(db: PowerSyncDrizzleDB, limit: number) {
  return db.select().from(themes).orderBy(desc(themes.is_dark), asc(themes.name)).limit(limit);
}

/**
 * Shared Drizzle query builder to fetch all themes.
 */
export function getThemesQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(themes).orderBy(asc(themes.created_at));
}
